/**
 * ProtectedRoute - Route guard for authenticated pages
 * 
 * Uses the global AuthContext for session validation.
 * No duplicate auth checks - relies on single source of truth.
 * 
 * FIXED: Now shows AuthRequiredScreen instead of redirecting to /login
 * This keeps the user in the app flow and allows them to sign in seamlessly.
 * 
 * Protected routes:
 * - /dashboard
 * - /chitragupt
 * - /chitragupt/session
 * - /profile
 */
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import AuthRequiredScreen from './AuthRequiredScreen';

const ProtectedRoute = ({ children, requireOnboarding = false }) => {
  const location = useLocation();
  const auth = useAuth();
  
  // Debug log on every protected route access
  React.useEffect(() => {
    console.log('[ProtectedRoute] Checking access to:', location.pathname);
    console.log('[ProtectedRoute] Auth state:', {
      isAuthenticated: auth?.isAuthenticated,
      authLoading: auth?.authLoading,
      authChecked: auth?.authChecked,
      hasUser: !!auth?.user
    });
  }, [location.pathname, auth?.isAuthenticated, auth?.authLoading, auth?.authChecked, auth?.user]);

  // Still loading auth state - show spinner
  if (auth?.authLoading || !auth?.authChecked) {
    console.log('[ProtectedRoute] Auth loading, showing spinner');
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0d0d1a] via-[#1a1a2e] to-[#0d0d1a] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-12 h-12 text-purple-500 animate-spin" />
          <p className="text-white/60 text-sm">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - show AuthRequiredScreen instead of redirecting
  // This keeps the user in the app flow
  if (!auth?.isAuthenticated) {
    console.log('[ProtectedRoute] Not authenticated, showing AuthRequiredScreen');
    
    // Store where user was trying to go for after login
    sessionStorage.setItem('authReturnPath', location.pathname);
    
    return (
      <AuthRequiredScreen
        onBack={() => {
          // Navigate back to home/onboarding using Navigate component logic
          // We'll use the window location to go back
          window.history.back();
        }}
        onSignIn={() => {
          // Redirect to login page with Google One Tap
          sessionStorage.setItem('authReturnPath', window.location.pathname);
          sessionStorage.setItem('authReturnView', 'dashboard');
          window.location.href = '/login';
        }}
      />
    );
  }

  // Check if onboarding is required and not complete
  if (requireOnboarding && auth?.user && !auth.user.onboarding_complete) {
    console.log('[ProtectedRoute] Onboarding required but not complete');
    // Let user continue to onboarding page using Navigate
    if (location.pathname !== '/onboarding') {
      return <Navigate to="/onboarding" state={{ user: auth.user }} replace />;
    }
  }

  console.log('[ProtectedRoute] Access granted to:', location.pathname);
  
  // Authenticated - render children with auth context available
  return children;
};

export default ProtectedRoute;
